import { useEffect, useState } from "react";
import { useCustomMutation, useTranslate } from "@refinedev/core";

import { AuthImage } from "./AuthImage";
import type { PortalAnimalDetail, PortalWizardField, PortalWizardRun, PortalWizardStep } from "./types";

// Wizard-invulflow voor het pleeggezinportaal — 1-op-1 gemodelleerd naar de backoffice-runner
// (frontend-react/wizard/src/WizardDier.jsx): titel "{naam} uitvoeren" + beschrijving, klikbare
// pill-stepper met ✓, smiley-beoordelingsschaal voor SELECT (scale default aan), Ja/Nee-knoppen
// voor BOOLEAN, textarea met tekenteller, dier-zijbalk met wizard-samenvatting en een voetbalk
// met ‹ Vorige / 📄 Opslaan als concept / Volgende › / Afronden ✓. Bewaren gaat via PATCH
// (per stap én als concept), afronden via POST /complete.

type OptionShape = string | { value: string; label?: string | null };

function optionValue(o: OptionShape): string {
  return typeof o === "string" ? o : o.value;
}
function optionLabel(o: OptionShape): string {
  return typeof o === "string" ? o : (o.label ?? o.value);
}

/* Beoordelingsschaal: smiley + kleur (groen→rood) op positie — kopie van de backoffice-runner. */
const SCHAAL_SMILEYS = ["😄", "🙂", "😐", "😟", "😠"];
function schaalSmiley(i: number, n: number): string {
  if (n <= 1) return "🙂";
  return SCHAAL_SMILEYS[Math.round((i / (n - 1)) * (SCHAAL_SMILEYS.length - 1))];
}
function schaalKleur(i: number, n: number): string {
  const t = n <= 1 ? 0 : i / (n - 1);
  return `hsl(${Math.round(125 - t * 125)}, 52%, 42%)`;
}

/** Keuzeknoppen (enkel- of meervoudig), met optionele smiley-schaal. */
function ChoiceButtons({
  field,
  value,
  onChange,
  multi,
}: {
  field: PortalWizardField;
  value: unknown;
  onChange: (v: unknown) => void;
  multi: boolean;
}) {
  const opties = field.options ?? [];
  // Schaal (smiley+kleur) default AAN voor enkele keuze, tenzij uitgezet — zoals de backoffice.
  const schaal = field.fieldType === "SELECT" ? field.scale !== false : field.scale === true;
  const arr: string[] = multi && Array.isArray(value) ? (value as string[]) : [];
  const kies = (v: string) => {
    if (multi) {
      onChange(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
    } else {
      onChange(value === v ? "" : v);
    }
  };
  return (
    <div className="fad-wizrun-keuzes">
      {opties.map((o, i) => {
        const v = optionValue(o);
        const gekozen = multi ? arr.includes(v) : value === v;
        const kleur = schaal ? schaalKleur(i, opties.length) : null;
        return (
          <button
            type="button"
            key={v}
            className={
              "fad-wizrun-keuze" + (gekozen ? " is-gekozen" : "") + (schaal ? " is-schaal" : "")
            }
            style={gekozen && kleur ? { borderColor: kleur } : undefined}
            onClick={() => kies(v)}
          >
            {schaal ? (
              <span className="fad-wizrun-smiley" style={{ color: kleur ?? undefined }}>
                {schaalSmiley(i, opties.length)}
              </span>
            ) : null}
            <span className="fad-wizrun-keuzelabel">{optionLabel(o)}</span>
          </button>
        );
      })}
    </div>
  );
}

export function PortalWizardRunner({
  base,
  run,
  wizardName,
  animal,
  age,
  onClose,
}: {
  /** API-basis: portal/animals/{org}/{dier} */
  base: string;
  run: PortalWizardRun;
  wizardName: string;
  /** Dier voor de zijbalk (foto + kerngegevens), zoals de backoffice-runner. */
  animal: PortalAnimalDetail;
  /** Leeftijdslabel zoals de detailpagina het toont (bv. "7j 5mnd"). */
  age: string | null;
  onClose: (completed: boolean) => void;
}) {
  const t = useTranslate();
  const { mutate, isLoading: saving } = useCustomMutation();
  const schema = run.schema;
  const steps: PortalWizardStep[] = schema?.steps ?? [];
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, unknown>>(() => ({ ...(run.data ?? {}) }));
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const step = steps[stepIndex];
  const lastStep = stepIndex >= steps.length - 1;

  const setAnswer = (fieldKey: string, value: unknown) =>
    setAnswers((a) => ({ ...a, [fieldKey]: value }));

  // Verplicht-check per stap; BOOLEAN is uitgezonderd (niet aangevinkt is een geldig antwoord),
  // zoals in de backoffice-runner.
  const missingRequired = (s: PortalWizardStep): string | null => {
    for (const f of s.fields) {
      if (!f.required || f.fieldType === "BOOLEAN") continue;
      const v = answers[f.fieldKey];
      if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) {
        return f.label || f.fieldKey;
      }
    }
    return null;
  };

  const patchThen = (onDone: () => void) => {
    setError(undefined);
    mutate(
      { url: `${base}/wizards/${run.id}`, method: "patch", values: { data: answers } },
      {
        onSuccess: onDone,
        onError: () => setError(t("portal.animalDetail.wizards.saveError")),
      },
    );
  };

  const next = () => {
    const missing = step ? missingRequired(step) : null;
    if (missing) {
      setError(`"${missing}" ${t("portal.animalDetail.wizards.requiredSuffix")}`);
      return;
    }
    patchThen(() => setStepIndex((i) => Math.min(i + 1, steps.length - 1)));
  };

  const previous = () => {
    setError(undefined);
    setStepIndex((i) => Math.max(i - 1, 0));
  };

  const saveDraft = () => {
    patchThen(() => onClose(false));
  };

  const finish = () => {
    const missing = step ? missingRequired(step) : null;
    if (missing) {
      setError(`"${missing}" ${t("portal.animalDetail.wizards.requiredSuffix")}`);
      return;
    }
    patchThen(() =>
      mutate(
        { url: `${base}/wizards/${run.id}/complete`, method: "post", values: {} },
        {
          onSuccess: () => onClose(true),
          onError: () => setError(t("portal.animalDetail.wizards.saveError")),
        },
      ),
    );
  };

  const renderField = (f: PortalWizardField) => {
    const value = answers[f.fieldKey];
    switch (f.fieldType) {
      case "TEXTAREA": {
        const len = ((value as string) ?? "").length;
        return (
          <div className="fad-wizrun-areawrap">
            <textarea
              className="fad-input fad-input--area"
              rows={3}
              maxLength={1000}
              placeholder={f.placeholder ?? undefined}
              value={(value as string) ?? ""}
              onChange={(e) => setAnswer(f.fieldKey, e.target.value)}
            />
            <span className="fad-wizrun-charcount">{len} / 1000</span>
          </div>
        );
      }
      case "NUMBER":
        return (
          <input
            className="fad-input"
            type="number"
            placeholder={f.placeholder ?? undefined}
            value={value == null ? "" : String(value)}
            onChange={(e) =>
              setAnswer(f.fieldKey, e.target.value === "" ? null : Number(e.target.value))
            }
          />
        );
      case "DATE":
        return (
          <input
            className="fad-input"
            type="date"
            value={(value as string) ?? ""}
            onChange={(e) => setAnswer(f.fieldKey, e.target.value || null)}
          />
        );
      case "BOOLEAN": {
        const sel = value === true ? "true" : value === false ? "false" : "";
        return (
          <div className="fad-wizrun-keuzes">
            {[
              ["true", t("portal.animalDetail.wizards.yes")],
              ["false", t("portal.animalDetail.wizards.no")],
            ].map(([v, l]) => (
              <button
                type="button"
                key={v}
                className={"fad-wizrun-keuze" + (sel === v ? " is-gekozen" : "")}
                onClick={() => setAnswer(f.fieldKey, v === "true")}
              >
                <span className="fad-wizrun-keuzelabel">{l}</span>
              </button>
            ))}
          </div>
        );
      }
      case "SELECT":
        return <ChoiceButtons field={f} value={value} onChange={(v) => setAnswer(f.fieldKey, v)} multi={false} />;
      case "MULTISELECT":
      case "CHECKBOX_GROUP":
        return <ChoiceButtons field={f} value={value} onChange={(v) => setAnswer(f.fieldKey, v)} multi={true} />;
      default:
        // TEXT en onbekende types: tekstinvoer
        return (
          <input
            className="fad-input"
            placeholder={f.placeholder ?? undefined}
            value={(value as string) ?? ""}
            onChange={(e) => setAnswer(f.fieldKey, e.target.value)}
          />
        );
    }
  };

  const sinceDays = (() => {
    if (!animal.since) return null;
    const ms = Date.now() - Date.parse(animal.since);
    return Number.isNaN(ms) ? null : Math.max(0, Math.floor(ms / 86_400_000));
  })();

  const naam = schema?.name || wizardName;

  return (
    <div className="fad-wizrun-overlay" onClick={() => onClose(false)}>
      <div
        className="fad-wizrun"
        role="dialog"
        aria-modal="true"
        aria-label={naam}
        onClick={(e) => e.stopPropagation()}
      >
        {/* kop: titel + beschrijving + sluiten */}
        <div className="fad-wizrun-kop">
          <div>
            <h2 className="fad-wizrun-titel">
              {naam} {t("portal.animalDetail.wizards.run")}
            </h2>
            {schema?.description ? <p className="fad-wizrun-oms">{schema.description}</p> : null}
          </div>
          <button
            type="button"
            className="fad-wizrun-sluit"
            aria-label={t("portal.animalDetail.edit.cancel")}
            onClick={() => onClose(false)}
          >
            ×
          </button>
        </div>

        {/* pill-stepper: klikbaar terug, ✓ voor afgeronde stappen */}
        {steps.length > 0 ? (
          <ol className="fad-wizrun-stapper">
            {steps.map((sdef, i) => (
              <li
                key={i}
                className={
                  "fad-wizrun-stap" + (i === stepIndex ? " is-actief" : i < stepIndex ? " is-klaar" : "")
                }
                aria-current={i === stepIndex ? "step" : undefined}
                onClick={() => {
                  if (i < stepIndex) {
                    setError(undefined);
                    setStepIndex(i);
                  }
                }}
              >
                <span className="fad-wizrun-stapnr">{i < stepIndex ? "✓" : i + 1}</span>
                <span className="fad-wizrun-staptekst">
                  <strong>{sdef.title || `${i + 1}`}</strong>
                  {sdef.description ? <span className="fad-wizrun-stapoms">{sdef.description}</span> : null}
                </span>
              </li>
            ))}
          </ol>
        ) : null}

        {/* hoofdlayout: velden links, dier-zijbalk rechts */}
        <div className="fad-wizrun-layout">
          <div className="fad-wizrun-main">
            {steps.length === 0 ? (
              <p className="fad-empty">{t("portal.animalDetail.wizards.noSchema")}</p>
            ) : (
              <div className="fad-wizrun-velden">
                {(step?.fields ?? []).map((f) => (
                  <div key={f.fieldKey} className="fad-wizrun-veld">
                    <span className="fad-wizrun-veldlabel">
                      {f.label || f.fieldKey}
                      {f.required ? <span className="fad-wizrun-ster">*</span> : null}
                    </span>
                    {renderField(f)}
                    {f.helpText ? <p className="fad-wizrun-hint">{f.helpText}</p> : null}
                  </div>
                ))}
                {(step?.fields ?? []).length === 0 ? (
                  <p className="fad-empty">{t("portal.animalDetail.wizards.noFields")}</p>
                ) : null}
              </div>
            )}
            {error ? (
              <p className="fad-wizrun-fout" role="alert">
                {error}
              </p>
            ) : null}
          </div>

          <aside className="fad-wizrun-zij">
            <div className="fad-wizrun-dierkaart">
              <AuthImage
                className="fad-wizrun-dierfoto"
                path={
                  animal.hasPhoto
                    ? `/portal/organizations/${animal.organizationUuid}/animals/${animal.dierUuid}/photo`
                    : null
                }
                alt={animal.name ?? ""}
                size="84px"
              />
              <div className="fad-wizrun-diernaam">
                <strong>{animal.name || "—"}</strong>
                <span className="fad-wizrun-muted">{animal.breed || animal.species || ""}</span>
              </div>
              {sinceDays != null ? (
                <span className="fad-wizrun-dierbadge">
                  {t("portal.animalDetail.wizards.side.withYouSince")} {sinceDays}{" "}
                  {t("portal.animalDetail.wizards.side.days")}
                </span>
              ) : null}
            </div>
            <dl className="fad-wizrun-dierinfo">
              {animal.chipNumber ? (
                <>
                  <dt>{t("portal.animalDetail.wizards.side.chip")}</dt>
                  <dd>{animal.chipNumber}</dd>
                </>
              ) : null}
              {animal.dateOfBirth ? (
                <>
                  <dt>{t("portal.animalDetail.wizards.side.birth")}</dt>
                  <dd>{animal.dateOfBirth}</dd>
                </>
              ) : null}
              {animal.sex ? (
                <>
                  <dt>{t("portal.animalDetail.wizards.side.sex")}</dt>
                  <dd>{animal.sex}</dd>
                </>
              ) : null}
              {age ? (
                <>
                  <dt>{t("portal.animalDetail.wizards.side.age")}</dt>
                  <dd>{age}</dd>
                </>
              ) : null}
            </dl>
            <div className="fad-wizrun-samenv">
              <div className="fad-wizrun-samenvkop">{t("portal.animalDetail.wizards.side.summary")}</div>
              <ol className="fad-wizrun-samenvlijst">
                {steps.map((sdef, i) => (
                  <li key={i} className={i === stepIndex ? "is-nu" : i < stepIndex ? "is-klaar" : ""}>
                    <span className="fad-wizrun-samenvnr">{i < stepIndex ? "✓" : i + 1}</span>
                    <span className="fad-wizrun-samenvtekst">
                      <strong>{sdef.title || `${i + 1}`}</strong>
                      <span className="fad-wizrun-muted">
                        {i < stepIndex
                          ? t("portal.animalDetail.wizards.side.statusDone")
                          : i === stepIndex
                            ? t("portal.animalDetail.wizards.side.statusBusy")
                            : t("portal.animalDetail.wizards.side.statusWait")}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        </div>

        {/* voet: ‹ Vorige | 📄 Opslaan als concept | (spacer) | Volgende › / Afronden ✓ */}
        <div className="fad-wizrun-voet">
          <button
            type="button"
            className="fad-btn"
            disabled={stepIndex === 0 || saving}
            onClick={previous}
          >
            ‹ {t("portal.animalDetail.wizards.previous")}
          </button>
          <button type="button" className="fad-btn" disabled={saving} onClick={saveDraft}>
            📄 {t("portal.animalDetail.wizards.saveDraft")}
          </button>
          <span className="fad-wizrun-spacer" />
          {lastStep ? (
            <button type="button" className="fad-btn fad-btn--primary" disabled={saving} onClick={finish}>
              {saving ? t("portal.animalDetail.edit.saving") : `${t("portal.animalDetail.wizards.finish")} ✓`}
            </button>
          ) : (
            <button type="button" className="fad-btn fad-btn--primary" disabled={saving} onClick={next}>
              {t("portal.animalDetail.wizards.next")} ›
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
